import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import { fg } from '@atlaskit/platform-feature-flags';
import { type ContentProps, Popup, type PopupProps, type TriggerProps } from '@atlaskit/popup';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useMenuStateContext } from '../../../context';
import {
	type EntryPointLoadTrigger,
	useUniversalCreateAnalytics,
} from '../../../hooks/use-universal-create-analytics';
import { GenericErrorBoundary } from '../../../services';
import type {
	TriggerPropsWithSplitPrimaryRef,
	UniversalCreateButtonCombinedProps,
} from '../../../types';

import { useClickToCloseRef } from './hooks/useClickToCloseRef';
import { usePrefetchV1 } from './hooks/usePrefetchV1';
import { usePrefetchV2 } from './hooks/usePrefetchV2';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomPopupProps = UnionOmit<PopupProps, 'isOpen' | 'content'>;
type EntryPointPropsType<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

export type Props<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointPropsType<TEntryPoint>, 'onClose' | 'contentProps' | 'isOpen'> &
		UniversalCreateButtonCombinedProps;
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	forcedReportErrorUFO?: boolean;
	onError?: ErrorCallback;
	trigger: (triggerProps: TriggerPropsWithSplitPrimaryRef) => ReactNode;
} & CustomPopupProps;

const noop = () => <></>;

function EntryPointPopupInner<TEntryPoint extends AnyEntryPoint>({
	trigger,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	onError,
	forcedReportErrorUFO,
	onClose,
	...popupProps
}: Props<TEntryPoint>) {
	const environmentProvider = useRelayEnvironmentProvider();

	const {
		controlledOpen,
		preventClose,
		product,
		crossAppCreationConfig,
		suggestionConfig,
		isV2Mode,
	} = useMenuStateContext();

	const v1PrefetchEnabled =
		suggestionConfig.enabled &&
		Boolean(suggestionConfig.prefetch) &&
		fg('navx_universal_create_cross_app_creation_enabled');
	const v2PrefetchEnabled =
		crossAppCreationConfig.enabled && Boolean(crossAppCreationConfig.prefetch);

	const { triggerPrefetch: triggerPrefetchV1 } = usePrefetchV1();
	const { triggerPrefetch: triggerPrefetchV2 } = usePrefetchV2();

	const id = useMemo(
		(): string => (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown',
		[entryPoint],
	);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<(contentProps: ContentProps) => ReactNode>(() => noop);

	const handleOpen = useCallback(() => {
		setIsOpen(true);
	}, []);

	const handleClose = useCallback(
		(
			event: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			if (product === 'confluence' && preventClose) {
				return;
			}

			setIsOpen(false);
			onClose?.(event, currentLevel);

			setContent(() => noop);
		},
		[preventClose, product, onClose],
	);

	const isPopupOpen = controlledOpen !== undefined ? controlledOpen : isOpen;

	const triggerCloseRef = useClickToCloseRef({
		isOpen: isPopupOpen,
		handleClose,
	});

	const prevIsOpenRef = useRef<boolean>(isPopupOpen);
	const entryPointLoadTriggeredRef = useRef(false);

	const { fireMenuStateChanged, fireEntryPointLoadTriggered } = useUniversalCreateAnalytics();

	useEffect(
		function fireMenuStateChangedEffect() {
			const prevIsOpen = prevIsOpenRef.current;

			// Only fire analytics on actual state transitions (use displayed state)
			if (isPopupOpen !== prevIsOpen) {
				fireMenuStateChanged({
					itemState: isPopupOpen ? 'expanded' : 'collapsed',
					version: isV2Mode ? 'v2' : 'v1',
				});
			}
			prevIsOpenRef.current = isPopupOpen;
		},
		[isPopupOpen, fireMenuStateChanged, prevIsOpenRef, isV2Mode],
	);

	const version = isV2Mode ? 'v2' : 'v1';

	// on hover callback
	const load = useCallback(
		(trigger: EntryPointLoadTrigger = 'hover') => {
			if (!entryPointLoadTriggeredRef.current) {
				entryPointLoadTriggeredRef.current = true;
				fireEntryPointLoadTriggered({ trigger, version });
			}

			if (v2PrefetchEnabled) {
				triggerPrefetchV2();
			} else if (v1PrefetchEnabled) {
				triggerPrefetchV1();
			}

			return loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			);
		},
		[
			entryPoint,
			entryPointParams,
			environmentProvider,
			fireEntryPointLoadTriggered,
			v1PrefetchEnabled,
			v2PrefetchEnabled,
			triggerPrefetchV1,
			triggerPrefetchV2,
			version,
		],
	);

	// on menu loaded callback
	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing popup
			if (isOpen) {
				return;
			}

			handleOpen();

			setContent(() => (contentProps: ContentProps) => (
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={fallback || null}
					errorFallback={errorFallback}
					runtimeProps={
						{
							entryPointProps,
							onClose: handleClose,
							contentProps,
						} as unknown as EntryPointPropsType<TEntryPoint>
					}
					onError={onError}
					forcedError={forcedReportErrorUFO}
				/>
			));
		},
		[
			setContent,
			isOpen,
			id,
			entryPointProps,
			fallback,
			forcedReportErrorUFO,
			errorFallback,
			handleOpen,
			onError,
			handleClose,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
	});

	// Primary button onLoad - preload but don't open popup
	const onLoadPrimary = useCallback(() => {
		// Primary button should only preload, NOT open the popup
		// The preloaded reference will be disposed automatically by usePressablePreloadRef
		// The actual click behavior is handled by primaryActionItem.onClick
	}, []);

	// Separate ref for the primary button with ONLY preload functionality (no popup opening)
	const primaryButtonRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad: onLoadPrimary,
	});

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			const mergedRef = mergeRefs([triggerRef, triggerProps.ref, triggerCloseRef]);
			return trigger({ ...triggerProps, ref: mergedRef, splitButtonPrimaryRef: primaryButtonRef });
		},
		[trigger, triggerRef, triggerCloseRef, primaryButtonRef],
	);

	const popupContent = useCallback(
		(contentProps: ContentProps) => content(contentProps),
		[content],
	);

	const prevControlledOpenRef = useRef<boolean | undefined>(undefined);

	useEffect(
		function preloadWithControlledOpenState() {
			if (controlledOpen !== undefined && controlledOpen !== prevControlledOpenRef.current) {
				prevControlledOpenRef.current = controlledOpen;

				if (isPopupOpen && !isOpen) {
					// When popup should be open (controlled) but we haven't loaded yet, trigger loading
					const triggerLoadingAndOpen = () => {
						const preloadedRef = load('controlled');
						if (preloadedRef) {
							onLoad({ reference: preloadedRef });
						}
					};

					// Schedule the loading after a tick to avoid synchronous DOM mutations during render
					const timeoutId = setTimeout(triggerLoadingAndOpen, 0);
					return () => clearTimeout(timeoutId);
				} else if (!isPopupOpen) {
					setIsOpen(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[controlledOpen, isOpen, isPopupOpen],
	);

	return (
		<Popup
			{...popupProps}
			isOpen={isPopupOpen}
			onClose={handleClose}
			trigger={popupTrigger}
			content={popupContent}
		/>
	);
}

export function EntryPointPopup<TEntryPoint extends AnyEntryPoint>(props: Props<TEntryPoint>) {
	return (
		<GenericErrorBoundary name="entry-point-popup">
			<EntryPointPopupInner {...props} />
		</GenericErrorBoundary>
	);
}
